/* eslint-disable max-lines */
import 'intro.js/introjs.css';
import '@/styles/tutorial.css';

import React, { FC, Fragment, lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRoutesWithBreadcrumbs } from '@/contexts/BreadcrumbsContext';
import {
  AccessDenied,
  ProtectedRoute,
  ProtectedRouteSimple,
  Unsubscribe,
} from '@/route';
import {
  ForgotPassword,
  InvitePin,
  ResetPassword,
  SetPassword,
  SignIn,
  UserInvite,
  VerifyEmail,
  VerifyEmailSuccess,
} from '@/route/Auth';
import { LocaleNavigate, LocaleSync } from '@/utils/locale';

import { DashboardLoadingIcon } from '../components/Loaders';
import { LocaleRedirect } from '../components/LocaleRedirect/LocaleRedirect';

const ActivityJournalEntrypoint = lazy(() => import('./ActivityJournal').then((m) => ({ default: m.ActivityJournalEntrypoint })));
const AIAgentChat = lazy(() => import('./AIAgentChat').then((m) => ({ default: m.AIAgentChat })));
const AiAgentsEntrypoint = lazy(() => import('./AiAgents/AiAgentsEntrypoint').then((m) => ({ default: m.AiAgentsEntrypoint })));
const AIUsageStatsView = lazy(() => import('./AIUsageStats').then((m) => ({ default: m.AIUsageStatsView })));
const AlertsEntrypoint = lazy(() => import('./Alerts/AlertsEntrypoint').then((m) => ({ default: m.AlertsEntrypoint })));
const AnalyticsEntrypoint = lazy(() => import('./Analitics').then((m) => ({ default: m.AnalyticsEntrypoint })));
const BreakfastServiceEntrypoint = lazy(() => import('./BreakfastService').then((m) => ({ default: m.BreakfastServiceEntrypoint })));
const ChatEntrypoint = lazy(() => import('./Chat').then((m) => ({ default: m.ChatEntrypoint })));
const ConferencesEntrypoint = lazy(() => import('./Conferences').then((m) => ({ default: m.ConferencesEntrypoint })));
const CreateOrganization = lazy(() => import('./CreateOrganization').then((m) => ({ default: m.CreateOrganization })));
const Dashboard = lazy(() => import('./Dashboard/Dashboard').then((m) => ({ default: m.Dashboard })));
const Error404 = lazy(() => import('./ErrorPages').then((m) => ({ default: m.Error404 })));
const EventsEntrypoint = lazy(() => import('./Events').then((m) => ({ default: m.EventsEntrypoint })));
const FaqEntrypoint = lazy(() => import('./Faq').then((m) => ({ default: m.FaqEntrypoint })));
const HelpChatEntrypoint = lazy(() => import('./HelpChat').then((m) => ({ default: m.HelpChatEntrypoint })));
const HotelEntrypoint = lazy(() => import('./Hotel').then((m) => ({ default: m.HotelEntrypoint })));
const InformantEntrypoint = lazy(() => import('./Informant').then((m) => ({ default: m.InformantEntrypoint })));
const AmenitiesEntrypoint = lazy(() => import('./Informant/pages/Amenities/AmenitiesEntrypoint').then((m) => ({ default: m.AmenitiesEntrypoint })));
const BusinessCardsEntrypoint = lazy(() => import('./Informant/pages/BusinessCards').then((m) => ({ default: m.BusinessCardsEntrypoint })));
const ChannelsEntrypoint = lazy(() => import('./Informant/pages/Channels/ChannelsEntrypoint').then((m) => ({ default: m.ChannelsEntrypoint })));
const CustomBlocksEntrypoint = lazy(() => import('./Informant/pages/CustomBlocks/CustomBlocksEntrypoint').then((m) => ({ default: m.CustomBlocksEntrypoint })));
const GoodPracticeEntrypoint = lazy(() => import('./Informant/pages/GoodPractice').then((m) => ({ default: m.GoodPracticeEntrypoint })));
const GuestFaqEntrypoint = lazy(() => import('./Informant/pages/GuestFaq/GuestFaqEntrypoint').then((m) => ({ default: m.GuestFaqEntrypoint })));
const LinksToReviewsEntrypoint = lazy(() => import('./Informant/pages/LinksToReviews').then((m) => ({ default: m.LinksToReviewsEntrypoint })));
const LoyaltyProgramEntrypoint = lazy(() => import('./Informant/pages/LoyaltyProgram').then((m) => ({ default: m.LoyaltyProgramEntrypoint })));
const PhoneBookEntrypoint = lazy(() => import('./Informant/pages/PhoneBook').then((m) => ({ default: m.PhoneBookEntrypoint })));
const RegulationsEntrypoint = lazy(() => import('./Informant/pages/Regulations').then((m) => ({ default: m.RegulationsEntrypoint })));
const SocialMediaEntrypoint = lazy(() => import('./Informant/pages/SocialMedia').then((m) => ({ default: m.SocialMediaEntrypoint })));
const VirtualTourEntrypoint = lazy(() => import('./Informant/pages/VirtualTour').then((m) => ({ default: m.VirtualTourEntrypoint })));
const WifEntrypoint = lazy(() => import('./Informant/pages/Wifi').then((m) => ({ default: m.WifEntrypoint })));
const MailslEntrypoint = lazy(() => import('./Mails').then((m) => ({ default: m.MailslEntrypoint })));
const MarketingEntrypoint = lazy(() => import('./Marketing').then((m) => ({ default: m.MarketingEntrypoint })));
const MarketingGuest = lazy(() => import('./Marketing/pages').then((m) => ({ default: m.MarketingGuest })));
const MediaLibraryEntrypoint = lazy(() => import('./MediaLibrary').then((m) => ({ default: m.MediaLibraryEntrypoint })));
const MuseumEntrypoint = lazy(() => import('./Museum').then((m) => ({ default: m.MuseumEntrypoint })));
const MyDevicesEntrypoint = lazy(() => import('./MyDevices').then((m) => ({ default: m.MyDevicesEntrypoint })));
const NotificationCenterEntrypoint = lazy(() => import('./NotificationsCenter').then((m) => ({ default: m.NotificationCenterEntrypoint })));
const OperaEntrypoint = lazy(() => import('./Opera').then((m) => ({ default: m.OperaEntrypoint })));
const OrderProductsEntrypoint = lazy(() => import('./OrderProducts').then((m) => ({ default: m.OrderProductsEntrypoint })));
const BreakfastOrdersEntrypoint = lazy(() => import('./BreakfastOrders').then((m) => ({ default: m.BreakfastOrdersEntrypoint })));
const OrdersEntrypoint = lazy(() => import('./Orders').then((m) => ({ default: m.OrdersEntrypoint })));
const PoiEntrypoint = lazy(() => import('./Poi').then((m) => ({ default: m.PoiEntrypoint })));
const PrivacyPolicyEntrypoint = lazy(() => import('./PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicyEntrypoint })));
const ProductRentalEntrypoint = lazy(() => import('./ProductRental').then((m) => ({ default: m.ProductRentalEntrypoint })));
const ReservationsEntrypoint = lazy(() => import('./ProductRental/pages').then((m) => ({ default: m.ReservationsEntrypoint })));
const ReservationsCenterEntrypoint = lazy(() => import('./Reservations').then((m) => ({ default: m.ReservationsCenterEntrypoint })));
const ProductsServicesEntrypoint = lazy(() => import('./ProductsServices').then((m) => ({ default: m.ProductsServicesEntrypoint })));
const QrGeneratorEntrypoint = lazy(() => import('./QrGenerator').then((m) => ({ default: m.QrGeneratorEntrypoint })));
const ReportsEntrypoint = lazy(() => import('./Reports').then((m) => ({ default: m.ReportsEntrypoint })));
const RoomServiceEntrypoint = lazy(() => import('./RoomService').then((m) => ({ default: m.RoomServiceEntrypoint })));
const SalesAnalyticsEntrypoint = lazy(() => import('./SalesAnalytics').then((m) => ({ default: m.SalesAnalyticsEntrypoint })));
const PaymentsEntrypoint = lazy(() => import('./Payments').then((m) => ({ default: m.PaymentsEntrypoint })));
const SelectOrganization = lazy(() => import('./SelectOrganization').then((m) => ({ default: m.SelectOrganization })));
const SettingsEntrypoint = lazy(() => import('./Settings').then((m) => ({ default: m.SettingsEntrypoint })));
const TeamEntrypoint = lazy(() => import('./Team').then((m) => ({ default: m.TeamEntrypoint })));
const SpaEntrypoint = lazy(() => import('./Spa').then((m) => ({ default: m.SpaEntrypoint })));
const SpecialOfferEntrypoint = lazy(() => import('./SpecialOffer').then((m) => ({ default: m.SpecialOfferEntrypoint })));
const StaffApp = lazy(() => import('./StaffApp').then((m) => ({ default: m.StaffApp })));
const SupportService = lazy(() => import('./SupportService').then((m) => ({ default: m.SupportService })));
const SurveysEntrypoint = lazy(() => import('./Surveys/SurveysEntrypoint').then((m) => ({ default: m.SurveysEntrypoint })));
const ProfileNoOrg = lazy(() => import('./UserProfile').then((m) => ({ default: m.ProfileNoOrg })));
const UserProfileEntrypoint = lazy(() => import('./UserProfile').then((m) => ({ default: m.UserProfileEntrypoint })));
const UserProfileStart = lazy(() => import('./UserProfile').then((m) => ({ default: m.UserProfileStart })));
const WelcomePromoCodesEntrypoint = lazy(() => import('./WelcomePromoCodes/WelcomePromoCodesEntrypoint').then((m) => ({ default: m.WelcomePromoCodesEntrypoint })));
const WidgetsEntrypoint = lazy(() => import('./Widgets/WidgetsEntrypoint').then((m) => ({ default: m.WidgetsEntrypoint })));

// Fallback pokazywany przy dociąganiu (lazy) chunku danej trasy.
// Używamy tego samego brandowego loadera co guardy auth (ProtectedRoute),
// żeby przy logowaniu nie mrugały dwa różne loadery jeden po drugim.
const RouteLoader: FC = () => <DashboardLoadingIcon />;

export const App: FC = () => {
  const { t } = useTranslation();

  // ROUTE
  const routes = useMemo(() => [
    // Redirect bare paths (without locale) to /:lang equivalent
    {
      path: '/',
      element: <LocaleRedirect />,
    },
    {
      path: '/login',
      element: <LocaleRedirect />,
    },
    {
      path: '/verify-email',
      element: <LocaleRedirect />,
    },
    {
      path: '/forgot-password',
      element: <LocaleRedirect />,
    },
    {
      path: '/reset-password',
      element: <LocaleRedirect />,
    },
    {
      path: '/user-invite',
      element: <LocaleRedirect />,
    },
    {
      path: '/invite-pin',
      element: <LocaleRedirect />,
    },
    {
      path: '/set-password',
      element: <LocaleRedirect />,
    },
    {
      path: '/unauthorized',
      element: <LocaleRedirect />,
    },
    /*
     * Wypisanie z wysyłek stoi POZA logowaniem i POZA prefiksem języka: link idzie w
     * stopce maila do człowieka, który zwykle nie ma otwartej sesji panelu, a adres w
     * mailu ma być krótki i stały (`/unsubscribe?email=...`), niezależny od języka.
     * Wersja z prefiksem zostaje na wypadek wejścia z panelu.
     */
    {
      path: '/unsubscribe',
      element: <Unsubscribe />,
    },
    // All routes under /:lang
    {
      path: '/:lang',
      element: <LocaleSync />,
      children: [
        {
          path: 'login',
          element: <SignIn />,
        },
        {
          path: 'unsubscribe',
          element: <Unsubscribe />,
        },
        {
          path: 'create-organization',
          element: <ProtectedRouteSimple />,
          children: [
            {
              index: true,
              element: <CreateOrganization />,
            },
          ],
        },
        {
          path: 'select-organization',
          element: <ProtectedRouteSimple />,
          children: [
            {
              index: true,
              element: <SelectOrganization />,
            },
          ],
        },
        {
          path: 'profile-no-org',
          element: <ProtectedRouteSimple />,
          children: [
            {
              index: true,
              element: <ProfileNoOrg />,
            },
          ],
        },
        {
          path: 'user-profile-start',
          element: <ProtectedRouteSimple />,
          children: [
            {
              index: true,
              element: <UserProfileStart />,
            },
          ],
        },
        {
          path: '',
          element: <ProtectedRoute />,
          label: t('routes.home'),
          children: [
            {
              index: true,
              element: <Dashboard />,
              label: t('routes.home'),
            },
            {
              path: 'hotel/*',
              element: <InformantEntrypoint />,
              label: t('routes.myHotel'),
            },
            {
              path: 'hotel/general-information/*',
              element: <HotelEntrypoint />,
              label: t('routes.myHotel'),
            },
            {
              path: 'hotel/business-card/*',
              element: <BusinessCardsEntrypoint />,
              label: t('routes.businessCards'),
            },
            {
              path: 'hotel/amenities/*',
              element: <AmenitiesEntrypoint />,
              label: t('routes.amenities'),
            },
            {
              path: 'hotel/good-practice/*',
              element: <GoodPracticeEntrypoint />,
              label: t('routes.goodPractice'),
            },
            {
              path: 'hotel/phonebook/*',
              element: <PhoneBookEntrypoint />,
              label: t('routes.phonebook'),
            },
            {
              path: 'hotel/wifi/*',
              element: <WifEntrypoint />,
              label: t('routes.wifi'),
            },
            {
              path: 'hotel/regulations/*',
              element: <RegulationsEntrypoint />,
              label: t('routes.regulations'),
            },
            {
              path: 'hotel/poi/*',
              element: <PoiEntrypoint />,
              label: t('routes.poi'),
            },
            {
              path: 'hotel/tv-chanel/*',
              element: <ChannelsEntrypoint />,
              label: t('routes.tvChannel'),
            },
            {
              path: 'hotel/faq/*',
              element: <GuestFaqEntrypoint />,
              label: t('routes.faq'),
            },
            {
              path: 'hotel/loyalty-program/*',
              element: <LoyaltyProgramEntrypoint />,
              label: t('routes.loyaltyProgram'),
            },
            {
              path: 'hotel/social-media/*',
              element: <SocialMediaEntrypoint />,
              label: t('routes.socialMedia'),
            },
            {
              path: 'hotel/virtual-tour/*',
              element: <VirtualTourEntrypoint />,
              label: t('routes.virtualTour'),
            },
            {
              path: 'hotel/link-to-reviews/*',
              element: <LinksToReviewsEntrypoint />,
              label: t('routes.linkToReviews'),
            },
            {
              path: 'hotel/custom-blocks/*',
              element: <CustomBlocksEntrypoint />,
              label: t('hotel.customBlocks.title'),
            },
            //* ========= END HOTEl ===== //
            {
              path: 'special-offer/*',
              element: <SpecialOfferEntrypoint />,
              label: t('routes.specialOffer'),
            },
            {
              path: 'alerts/*',
              element: <AlertsEntrypoint />,
              label: t('routes.alerts'),
            },
            {
              path: 'promo-codes/*',
              element: <WelcomePromoCodesEntrypoint />,
              label: t('routes.promoCodes'),
            },
            {
              path: 'events/*',
              element: <EventsEntrypoint />,
              label: t('routes.events'),
            },
            {
              path: 'surveys/*',
              element: <SurveysEntrypoint />,
              label: t('routes.surveys'),
            },
            {
              path: 'ai-agents/*',
              element: <AiAgentsEntrypoint />,
              label: t('aiAgents.pageTitle', 'AI Agents'),
            },
            {
              path: 'widgets/*',
              element: <WidgetsEntrypoint />,
              label: t('widgets.pageTitle', 'Widgety'),
            },
            {
              path: 'qr-generator/*',
              element: <QrGeneratorEntrypoint />,
              label: t('qrGenerator.title', 'Room Mapping'),
            },
            {
              path: 'take-to-room-service/*',
              element: <RoomServiceEntrypoint />,
              label: t('routes.roomService'),
            },
            {
              path: 'breakfast-service/*',
              element: <BreakfastServiceEntrypoint />,
              label: t('routes.breakfastService', 'Serwis śniadaniowy'),
            },
            {
              path: 'order-products/*',
              element: <OrderProductsEntrypoint />,
              label: t('routes.orderProducts'),
            },
            {
              path: 'products-services/*',
              element: <ProductsServicesEntrypoint />,
              label: t('routes.productsServices'),
            },
            {
              path: 'product-rental/*',
              element: <ProductRentalEntrypoint />,
              label: t('routes.productRental'),
            },
            {
              path: 'product-rental-reservations/*',
              element: <ReservationsEntrypoint />,
              label: t('productRental.reservations.allReservations'),
            },
            {
              path: 'reservations/*',
              element: <ReservationsCenterEntrypoint />,
              label: t('routes.reservations', 'Rezerwacje'),
            },
            {
              path: 'products-services-old/*',
              element: <ProductsServicesEntrypoint />,
              label: t('routes.productsServices'),
            },
            {
              path: 'ai-agent-chat',
              element: <ChatEntrypoint />,
              label: t('routes.aiAgentChat'),
            },
            {
              path: 'marketing/*',
              element: <MarketingEntrypoint />,
              label: t('routes.marketing'),
            },
            {
              path: 'subscribers',
              element: <MarketingGuest />,
              label: t('routes.subscribers'),
            },
            {
              path: 'conferences/*',
              element: <ConferencesEntrypoint />,
              label: t('routes.conferences'),
            },
            {
              path: 'spa/*',
              element: <SpaEntrypoint />,
              label: t('routes.spa'),
            },
            {
              path: 'orders/*',
              element: <OrdersEntrypoint />,
              label: t('routes.orders'),
            },
            {
              path: 'breakfast-orders/*',
              element: <BreakfastOrdersEntrypoint />,
              label: t('routes.breakfastOrders', 'Śniadania'),
            },
            {
              path: 'reports/*',
              element: <ReportsEntrypoint />,
              label: t('routes.reports'),
            },
            {
              path: 'my-devices/*',
              element: <MyDevicesEntrypoint />,
              label: t('routes.myDevices'),
            },
            {
              path: 'analytics',
              element: <AnalyticsEntrypoint />,
              label: t('routes.analytics'),
            },
            {
              path: 'sales-analytics',
              element: <SalesAnalyticsEntrypoint />,
              label: t('routes.salesAnalytics'),
            },
            {
              /* Gwiazdka jest tu WARUNKIEM, nie ozdobą: moduł ma własny router
                 (transakcje + zwroty), a bez wildcardu `/payments/refunds` nie
                 pasuje do niczego i widok wychodzi pusty. */
              path: 'payments/*',
              element: <PaymentsEntrypoint />,
              label: t('routes.payments', 'Płatności'),
            },
            {
              path: 'activity-journal',
              element: <ActivityJournalEntrypoint />,
              label: t('routes.activityJournal', 'Dziennik aktywności'),
            },
            {
              path: 'media-library/*',
              element: <MediaLibraryEntrypoint />,
              label: t('routes.mediaLibrary'),
            },
            {
              path: 'ai-usage',
              element: <AIUsageStatsView />,
              label: t('routes.aiUsage'),
            },
            {
              path: 'ai-agent',
              element: <AIAgentChat />,
              label: t('routes.aiAgent'),
            },
            {
              path: 'opera/*',
              element: <OperaEntrypoint />,
              label: t('routes.opera', 'Opera Cloud'),
            },
            {
              // Zarządzanie zespołem: lista + formularze jako osobne widoki.
              // Ustawienia mają już tylko przycisk prowadzący tutaj.
              path: 'team/*',
              element: <TeamEntrypoint />,
              label: t('routes.team', 'Zespół'),
            },
            {
              path: 'settings/*',
              element: <SettingsEntrypoint />,
              label: t('routes.settings'),
            },
            {
              path: 'help',
              element: <HelpChatEntrypoint />,
              label: t('routes.help'),
            },
            //* ========= END SIDEBAR MENU ===== //
            {
              path: 'my-profile/*',
              element: <UserProfileEntrypoint />,
              label: t('routes.myProfile'),
            },
            {
              path: 'support-service',
              element: <SupportService />,
              label: t('routes.supportService'),
            },
            {
              path: 'staff-app',
              element: <StaffApp />,
              label: t('routes.staffApp', 'Aplikacja dla zespołu'),
            },
            {
              path: 'faq-help/*',
              element: <FaqEntrypoint />,
              label: t('routes.faq'),
            },
            {
              path: 'centrum-powiadomien/*',
              element: <NotificationCenterEntrypoint />,
              label: t('routes.notificationsCenter'),
            },
            {
              path: 'museum/*',
              element: <MuseumEntrypoint />,
              label: t('routes.museum'),
            },
            {
              path: '*',
              label: t('routes.error'),
              element: <Error404 />,
            },
            {
              path: 'privacy-policy',
              element: <PrivacyPolicyEntrypoint />,
              label: t('routes.privacyPolicy'),
            },
            {
              path: 'wiadomosci/*',
              element: <MailslEntrypoint />,
              label: t('routes.mails'),
            },
          ],
        },
        {
          path: 'verify-email',
          element: <VerifyEmail />,
        },
        {
          path: 'verify-email-success',
          element: <VerifyEmailSuccess />,
        },
        {
          path: 'forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
        {
          path: 'user-invite',
          element: <UserInvite />,
        },
        {
          path: 'invite-pin',
          element: <InvitePin />,
        },
        {
          path: 'set-password',
          element: <SetPassword />,
        },
        {
          path: 'unauthorized',
          element: <AccessDenied />,
        },
      ],
    },
  ], [t]);

  const routed = useRoutesWithBreadcrumbs(routes);
  const element = <Suspense fallback={<RouteLoader />}>{routed}</Suspense>;

  return (
    <Fragment key="router-render">
      {element}
    </Fragment>
  );
};
